import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store.js';
import VinScanner from '../components/VinScanner.js';
import PhotoCapture from '../components/PhotoCapture.js';
import InstallBanner from '../components/InstallBanner.js';
import { api } from '../services/api-client.js';
import SEO from '../components/SEO.js';
import { usePageTracker } from '../hooks/usePageTracker.js';

interface VinDecode {
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  engine: string | null;
  transmission: string | null;
  drivetrain: string | null;
  body_style: string | null;
  fuel_type: string | null;
}

// Parse NHTSA response into our decode shape
function parseNhtsa(results: any[]): VinDecode {
  const get = (id: number) => results.find((r: any) => r.VariableId === id)?.Value || null;
  const year = parseInt(get(29)) || null;
  const make = get(26);
  const model = get(28);
  const trim = get(109);
  const displacement = get(13);
  const cylinders = get(9);
  const engine = displacement && cylinders ? `${displacement}L ${cylinders}-Cyl` : displacement ? `${displacement}L` : null;
  const trans = get(37);
  const drive = get(15);
  const body = get(5);
  const fuel = get(24);
  return { year, make, model, trim, engine, transmission: trans, drivetrain: drive, body_style: body, fuel_type: fuel };
}

type Step = 'scan' | 'info' | 'photos' | 'activating' | 'done';

export default function QuickListPage() {
  usePageTracker('/go');

  const [step, setStep] = useState<Step>('scan');
  const [vin, setVin] = useState('');
  const [decode, setDecode] = useState<VinDecode | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [price, setPrice] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [vehicleTitle, setVehicleTitle] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [listingUrl, setListingUrl] = useState('');
  const setAuth = useAuthStore((s) => s.setAuth);

  // Step 1: VIN scanned → decode via NHTSA
  const handleVinScanned = async (scannedVin: string) => {
    setVin(scannedVin);
    setDecoding(true);
    setError('');
    try {
      const res = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/decodevin/${scannedVin}?format=json`);
      const data = await res.json();
      const parsed = parseNhtsa(data.Results);
      setDecode(parsed);
      const title = [parsed.year, parsed.make, parsed.model, parsed.trim].filter(Boolean).join(' ');
      setVehicleTitle(title || scannedVin);
      setStep('info');
    } catch {
      setError('Could not decode VIN. Please check and try again.');
    }
    setDecoding(false);
  };

  // Format phone as (xxx) xxx-xxxx
  const formatPhone = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Step 2: Submit info → quick-start
  const handleSubmit = async () => {
    if (!name.trim() || !phone.replace(/\D/g, '') || !price) {
      setError('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    setError('');

    const priceCents = Math.round(parseFloat(price.replace(/[^0-9.]/g, '')) * 100);
    const cleanPhone = phone.replace(/\D/g, '');

    try {
      const res = await api.post<any>('/auth/quick-start', {
        vin,
        name: name.trim(),
        phone: cleanPhone,
        asking_price_cents: priceCents,
        condition: 'good',
      });

      if (!res.success) {
        setError(res.error ?? 'Something went wrong');
        setSubmitting(false);
        return;
      }

      // Log user in
      setAuth(res.data.token, res.data.user, res.data.dealership);
      setVehicleId(res.data.vehicle.id);

      const title = [res.data.vehicle.year, res.data.vehicle.make, res.data.vehicle.model].filter(Boolean).join(' ');
      setVehicleTitle(title || 'Your Vehicle');
      setStep('photos');
    } catch {
      setError('Connection error. Please try again.');
    }
    setSubmitting(false);
  };

  // Step 3: Photos done → activate
  const handlePhotosDone = async (_count: number, mileage: number, notes: string) => {
    setStep('activating');
    try {
      await api.patch(`/vehicles/${vehicleId}`, {
        status: 'active',
        mileage,
        description: notes || undefined,
      });
      setListingUrl(`/vehicles/${vehicleId}`);
      setStep('done');
    } catch {
      setListingUrl(`/vehicles/${vehicleId}`);
      setStep('done');
    }
  };

  // Share listing
  const shareListing = () => {
    const url = `${window.location.origin}/browse/${vehicleId}`;
    if (navigator.share) {
      navigator.share({ title: vehicleTitle, text: `Check out my ${vehicleTitle} on AutoCarPost!`, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <div className="min-h-screen bg-surface-900 text-white">
      <SEO title="Quick List Your Car" description="The fastest way to create a car listing. Enter your VIN and let AI do the rest." path="/quick-list" />
      {/* Header */}
      <header className="sticky top-0 z-30 bg-surface-900/95 backdrop-blur-sm border-b border-surface-800/60">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold tracking-tight">AutoCarPost</Link>
          {step !== 'done' && (
            <div className="flex gap-1.5">
              {['scan', 'info', 'photos'].map((s, i) => (
                <div key={s} className={`w-8 h-1.5 rounded-full transition-colors ${
                  ['scan', 'info', 'photos', 'activating', 'done'].indexOf(step) >= i ? 'bg-brand-500' : 'bg-surface-700'
                }`} />
              ))}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6">

        {/* Step 1: Scan VIN */}
        {step === 'scan' && (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold">Scan Your VIN</h2>
              <p className="text-surface-500 text-sm mt-1">Point camera at the VIN barcode on your windshield</p>
            </div>
            <VinScanner onVinScanned={handleVinScanned} />
            {decoding && (
              <div className="flex items-center justify-center gap-2 py-4">
                <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-surface-400 text-sm">Decoding VIN...</span>
              </div>
            )}
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          </div>
        )}

        {/* Step 2: Vehicle Info + Your Details */}
        {step === 'info' && decode && (
          <div className="space-y-6">
            {/* Decoded vehicle card */}
            <div className="card p-5 border-brand-700 bg-brand-900/10">
              <h2 className="text-xl font-bold">{vehicleTitle}</h2>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-sm">
                {decode.engine && <div><span className="text-surface-500">Engine</span> <span className="ml-1">{decode.engine}</span></div>}
                {decode.transmission && <div><span className="text-surface-500">Trans</span> <span className="ml-1">{decode.transmission}</span></div>}
                {decode.drivetrain && <div><span className="text-surface-500">Drive</span> <span className="ml-1">{decode.drivetrain}</span></div>}
                {decode.body_style && <div><span className="text-surface-500">Body</span> <span className="ml-1">{decode.body_style}</span></div>}
              </div>
              <button onClick={() => { setStep('scan'); setDecode(null); setVin(''); }} className="text-brand-400 text-xs mt-3 hover:text-brand-300">
                Wrong car? Scan again
              </button>
            </div>

            {/* Your info */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-surface-400 uppercase tracking-wider">Your Info</h3>
              <input
                className="input w-full text-lg"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
              <input
                className="input w-full text-lg"
                placeholder="Phone Number"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
              />
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-500 text-lg">$</span>
                <input
                  className="input w-full text-lg pl-8"
                  placeholder="Asking Price"
                  type="text"
                  inputMode="numeric"
                  value={price}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '');
                    setPrice(raw ? parseInt(raw).toLocaleString() : '');
                  }}
                />
              </div>
            </div>

            {error && <p className="text-red-400 text-sm text-center">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl py-4 text-lg transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating your listing...
                </span>
              ) : 'List My Car'}
            </button>

            <p className="text-surface-600 text-xs text-center">
              Free forever. Your account is auto-created with your phone number.
            </p>
          </div>
        )}

        {/* Step 3: Photos */}
        {step === 'photos' && vehicleId && (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold">Add Photos</h2>
              <p className="text-surface-500 text-sm mt-1">Snap pics of your {vehicleTitle}. More photos = more buyers.</p>
            </div>
            <PhotoCapture
              vehicleId={vehicleId}
              vehicleTitle={vehicleTitle}
              onDone={handlePhotosDone}
            />
          </div>
        )}

        {/* Step 4: Activating */}
        {step === 'activating' && (
          <div className="text-center py-20 space-y-4">
            <div className="w-16 h-16 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <h2 className="text-2xl font-bold">Going Live...</h2>
            <p className="text-surface-400">AI is writing your listing, researching market prices, and analyzing your photos.</p>
          </div>
        )}

        {/* Step 5: Done! */}
        {step === 'done' && (
          <div className="text-center py-12 space-y-6">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-bold">Your Car is Live!</h2>
              <p className="text-surface-400 mt-2">{vehicleTitle} is now listed on AutoCarPost</p>
            </div>

            <div className="space-y-3 max-w-xs mx-auto">
              <Link
                to={listingUrl}
                className="block w-full bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl py-4 text-center transition-colors"
              >
                View Your Listing
              </Link>
              <button
                onClick={shareListing}
                className="w-full bg-surface-800 border border-surface-700 hover:border-brand-500 text-white font-semibold rounded-xl py-3 transition-colors"
              >
                Share Listing
              </button>
              <InstallBanner compact />
              <Link to="/dashboard" className="block text-surface-500 hover:text-brand-400 text-sm transition-colors">
                Go to Dashboard
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
